import { getMatchGroup, stripLabelQuotes } from './flowchart-parser-labels';
import type { FlowchartNodeStyle } from './flowchart-parser-model';
import { currentParent, type FlowchartParserState } from './flowchart-parser-state';

interface ParsedSubgraphHeader {
  id: string;
  rawTitle: string | null;
}

interface SubgraphHeaderParserInput {
  line: string;
  fallbackId: string;
}

type SubgraphHeaderParser = (input: SubgraphHeaderParserInput) => ParsedSubgraphHeader | null;

const SUBGRAPH_ID_TITLE_REGEX =
  /^subgraph\s+([A-Za-z0-9_][A-Za-z0-9_-]*)\s*(?:\[([\s\S]+)\]|"([\s\S]+)")\s*$/i;
const SUBGRAPH_BARE_REGEX = /^subgraph\s+([A-Za-z0-9_][A-Za-z0-9_-]*)\s*$/i;
const SUBGRAPH_TITLE_ONLY_REGEX = /^subgraph\s+(.+?)\s*$/i;
const SUBGRAPH_END_REGEX = /^end$/i;

function parseIdTitleSubgraphHeader(input: SubgraphHeaderParserInput): ParsedSubgraphHeader | null {
  const match = input.line.match(SUBGRAPH_ID_TITLE_REGEX);
  if (!match) {
    return null;
  }

  const id = getMatchGroup({ match, index: 1 }) ?? input.fallbackId;
  const bracketTitle = getMatchGroup({ match, index: 2 });
  const quotedTitle = getMatchGroup({ match, index: 3 });
  return { id, rawTitle: bracketTitle ?? quotedTitle };
}

function parseBareSubgraphHeader(input: SubgraphHeaderParserInput): ParsedSubgraphHeader | null {
  const match = input.line.match(SUBGRAPH_BARE_REGEX);
  if (!match) {
    return null;
  }

  const id = getMatchGroup({ match, index: 1 });
  return { id: id ?? input.fallbackId, rawTitle: id };
}

function parseTitleOnlySubgraphHeader(input: SubgraphHeaderParserInput): ParsedSubgraphHeader | null {
  const match = input.line.match(SUBGRAPH_TITLE_ONLY_REGEX);
  return match
    ? { id: input.fallbackId, rawTitle: getMatchGroup({ match, index: 1 }) }
    : null;
}

const SUBGRAPH_HEADER_PARSERS: ReadonlyArray<SubgraphHeaderParser> = [
  parseIdTitleSubgraphHeader,
  parseBareSubgraphHeader,
  parseTitleOnlySubgraphHeader,
];

function parseSubgraphHeader(input: {
  line: string;
  fallbackIndex: number;
}): ParsedSubgraphHeader {
  const fallbackId = `__sg${input.fallbackIndex}`;
  const parserInput = { line: input.line, fallbackId };
  return (
    SUBGRAPH_HEADER_PARSERS.reduce<ParsedSubgraphHeader | null>(
      (header, parser) => header || parser(parserInput),
      null,
    ) ?? { id: fallbackId, rawTitle: null }
  );
}

function readPendingClassNames(input: {
  id: string;
  pendingClassNamesById: Map<string, string[]>;
}): string[] {
  return input.pendingClassNamesById.get(input.id) ?? [];
}

function readPendingStyle(input: {
  id: string;
  pendingStylesById: Map<string, FlowchartNodeStyle>;
}): FlowchartNodeStyle | null {
  return input.pendingStylesById.get(input.id) ?? null;
}

export function isSubgraphHeaderLine(line: string): boolean {
  return (
    line.match(SUBGRAPH_ID_TITLE_REGEX) != null ||
    line.match(SUBGRAPH_BARE_REGEX) != null ||
    (/^subgraph\b/i.test(line) && line.match(SUBGRAPH_TITLE_ONLY_REGEX) != null)
  );
}

export function isSubgraphEndLine(line: string): boolean {
  return SUBGRAPH_END_REGEX.test(line);
}

export function pushSubgraph(input: {
  line: string;
  state: FlowchartParserState;
}): void {
  const header = parseSubgraphHeader({
    line: input.line,
    fallbackIndex: input.state.subgraphs.length,
  });
  const id = header.id;
  const pendingClassNames = readPendingClassNames({
    id,
    pendingClassNamesById: input.state.pendingClassNamesById,
  });
  const pendingStyle = readPendingStyle({
    id,
    pendingStylesById: input.state.pendingStylesById,
  });
  input.state.subgraphs.push({
    id,
    label: header.rawTitle ? stripLabelQuotes(header.rawTitle) : null,
    parent: currentParent(input.state.stack),
    classNames: pendingClassNames,
    inlineStyle: pendingStyle,
  });
  input.state.pendingClassNamesById.delete(id);
  input.state.pendingStylesById.delete(id);
  input.state.stack.push(id);
}
